import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CardSummary, receipts } from "../api/client";
import MoneyTotals from "../components/MoneyTotals";

/** Stable identity for a card. Brand is part of the key (and may be blank), so
 *  "Visa ····1234" and "····1234" are distinct cards — matching how the backend
 *  groups receipts on (brand, last4). */
function cardKey(c: CardSummary): string {
  return `${c.card_brand ?? ""}|${c.card_last4}`;
}

function cardLabel(c: CardSummary): string {
  return `${c.card_brand ?? "Card"} ···· ${c.card_last4}`;
}

export default function Cards() {
  const [items, setItems] = useState<CardSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Combine flow: the card being merged away, plus the chosen destination key.
  const [source, setSource] = useState<CardSummary | null>(null);
  const [destKey, setDestKey] = useState<string>("");
  const [merging, setMerging] = useState(false);

  function load() {
    setLoading(true);
    receipts
      .listCards()
      .then((rows) => setItems(rows))
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  function startCombine(c: CardSummary) {
    setSource(c);
    setDestKey("");
    setError(null);
    setNotice(null);
  }

  function cancelCombine() {
    setSource(null);
    setDestKey("");
  }

  async function confirmCombine() {
    if (!source) return;
    const dest = items.find((c) => cardKey(c) === destKey);
    if (!dest) {
      setError("Choose a destination card.");
      return;
    }
    if (
      !confirm(
        `Combine ${cardLabel(source)} into ${cardLabel(dest)}?\n\n` +
          `All ${source.receipt_count} receipt(s) on ${cardLabel(source)} will be ` +
          `moved to ${cardLabel(dest)}, and ${cardLabel(source)} will be removed.\n\n` +
          `This cannot be undone.`
      )
    ) {
      return;
    }
    setMerging(true);
    setError(null);
    try {
      const res = await receipts.mergeCards(
        { card_last4: source.card_last4, card_brand: source.card_brand },
        { card_last4: dest.card_last4, card_brand: dest.card_brand }
      );
      setNotice(
        `Moved ${res.moved} receipt(s) from ${cardLabel(source)} into ${cardLabel(dest)}.`
      );
      cancelCombine();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not combine cards");
    } finally {
      setMerging(false);
    }
  }

  if (loading) return <div className="empty-state">Loading…</div>;

  // Destinations = every card except the one being merged away.
  const destinations = source
    ? items.filter((c) => cardKey(c) !== cardKey(source))
    : [];

  return (
    <div className="page">
      <div className="page-header">
        <h1>Cards</h1>
      </div>
      <p className="page-sub" style={{ marginBottom: 16 }}>
        Each card we've seen in a receipt body (e.g. "Visa ending in 8906"). Click a
        card to see every receipt charged to it. We'll soon map each card onto an
        accounting-service account for spend rollups.
      </p>
      {error && <div className="page-error">{error}</div>}
      {notice && (
        <div className="muted" style={{ marginBottom: 12 }}>
          {notice}
        </div>
      )}

      {source && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Combine {cardLabel(source)}</h3>
          <p className="page-sub" style={{ marginTop: 0 }}>
            Move all {source.receipt_count} receipt(s) from{" "}
            <strong>{cardLabel(source)}</strong> onto another card. The receipts
            keep all their data — only the card they're filed under changes.
          </p>
          <div
            className="page-error"
            style={{ marginBottom: 12 }}
            role="alert"
          >
            ⚠ This is irreversible. {cardLabel(source)} will be permanently
            removed and its receipts re-stamped with the destination card. There
            is no undo.
          </div>
          {destinations.length === 0 ? (
            <div className="page-empty">
              No other card to combine into. You need at least two cards.
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label htmlFor="combine-dest">Combine into:</label>
              <select
                id="combine-dest"
                value={destKey}
                onChange={(e) => setDestKey(e.target.value)}
                disabled={merging}
              >
                <option value="">Select destination card…</option>
                {destinations.map((c) => (
                  <option key={cardKey(c)} value={cardKey(c)}>
                    {cardLabel(c)} ({c.receipt_count})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={confirmCombine}
                disabled={merging || !destKey}
              >
                {merging ? "Combining…" : "Combine cards"}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={cancelCombine}
                disabled={merging}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="page-empty">No card references detected yet.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Card</th>
              <th>Receipts</th>
              <th style={{ textAlign: "right" }}>Total spent</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={cardKey(c)}>
                <td>
                  <Link to={`/app/cards/${c.card_last4}`}>{cardLabel(c)}</Link>
                </td>
                <td>{c.receipt_count}</td>
                <td style={{ textAlign: "right" }}>
                  <MoneyTotals totals={c.totals} />
                </td>
                <td style={{ textAlign: "right" }}>
                  {items.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => startCombine(c)}
                      disabled={merging}
                      title="Combine this card into another"
                    >
                      Combine
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
